import { useEffect, useRef, useState } from 'react';
import { POLL_INTERVAL_MS } from './useSoteriaChat';

/**
 * useRevealedText
 * ---------------
 * Reveals a growing string one word at a time, so a reply that arrives in
 * poll-sized chunks reads as if it were typed.
 *
 * WHY THIS EXISTS. The reply is streamed server-side but delivered by polling
 * every POLL_INTERVAL_MS, so the text can only ever land in ~1.2s jumps. On a
 * short answer that looked like two chunks appearing, not streaming. Rather than
 * poll harder — which would multiply requests and database round trips for a
 * purely cosmetic gain — the chunks are buffered here and paid out gradually.
 *
 * THE RULE THAT MAKES IT WORK: an arriving chunk only ever extends the target.
 * It never pushes the revealed position forward. So a poll landing mid-animation
 * is invisible: the text keeps flowing at the same cadence instead of snapping
 * ahead, which is what would give the game away.
 *
 * PACING. The step is derived from how much text is waiting: the backlog is
 * spread across roughly one poll interval, clamped to a readable range. A big
 * backlog types faster, a nearly-caught-up one slows to a natural pace, and the
 * reveal can't drift so far behind that it is still typing long after the answer
 * is in. Once the turn ends it drains at full speed — a finished reply must not
 * be gated behind an animation.
 *
 * Returns the slice to render. The caller keeps the whole text; this only
 * affects what is painted, so nothing is lost on a reload or a refetch.
 */

// Fastest and slowest we will advance while text is still arriving. The upper
// bound keeps a nearly-caught-up reveal from crawling; the lower keeps a big
// backlog from becoming a blur.
const MIN_STEP_MS = 12;
const MAX_STEP_MS = 70;
// Once the turn is done there is nothing left to wait for, so empty the buffer
// as fast as React will repaint.
const DRAIN_STEP_MS = 6;

const isSpace = (ch: string): boolean => /\s/.test(ch);

/** Index just past the next word (plus any spaces after it, but not newlines). */
export function nextWordBoundary(text: string, from: number): number {
  if (from >= text.length) return text.length;
  let i = from;
  while (i < text.length && isSpace(text[i])) i++;
  while (i < text.length && !isSpace(text[i])) i++;
  // Carry trailing spaces with the word so the next one doesn't appear to jitter
  // in. Newlines are left for the following step, which keeps paragraph breaks
  // landing cleanly rather than mid-flow.
  while (i < text.length && text[i] === ' ') i++;
  return i;
}

/** How many words are still hidden — the backlog the pacing reacts to. */
function wordsRemaining(text: string, from: number): number {
  let count = 0;
  let i = from;
  while (i < text.length) {
    i = nextWordBoundary(text, i);
    count++;
  }
  return count;
}

export function useRevealedText(text: string, streaming: boolean): string {
  // Mounting on an already-finished message (scrolling back through history, or
  // a reload) must show it whole — only a reply that is live when it first
  // renders gets typed out.
  const [revealed, setRevealed] = useState(() => (streaming ? 0 : text.length));
  const previousText = useRef(text);

  useEffect(() => {
    if (!text.startsWith(previousText.current)) {
      // The text was REPLACED, not extended: the server discards a round of
      // prose when the answer proper starts (see services._run_streaming), and
      // the final scrubbed reply can differ from what was streamed. Clamp
      // instead of restarting from zero — re-typing an answer the user has
      // already begun reading is worse than letting it change in place.
      setRevealed((r) => Math.min(r, text.length));
    }
    previousText.current = text;
  }, [text]);

  useEffect(() => {
    if (revealed >= text.length) return;
    const step = streaming
      ? Math.min(
          MAX_STEP_MS,
          Math.max(
            MIN_STEP_MS,
            POLL_INTERVAL_MS / Math.max(1, wordsRemaining(text, revealed)),
          ),
        )
      : DRAIN_STEP_MS;

    // One timer per step rather than an interval: the delay is recomputed from
    // the current backlog each time, so pacing tracks the text as it arrives.
    const timer = setTimeout(
      () => setRevealed((r) => nextWordBoundary(text, r)),
      step,
    );
    return () => clearTimeout(timer);
  }, [text, revealed, streaming]);

  return revealed >= text.length ? text : text.slice(0, revealed);
}
